import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { get } from 'lodash';
import { Modal } from 'react-bootstrap';
import './sccs/FormDoubleInput.scss';
interface IFormDoubleInputProps {
    label: string;
    name: string;
    double?: boolean;
    /**
     * Define el tipo de input por columna.
     * - 1: Campo de texto
     * - 2: Campo enteros
     * - 3: Campo decimales (con . o ,)
     * - 4: Campo de fecha
     * - 5: Campo de Si o No
     * 
     * Ejemplo: [1, 2] = primera columna texto, segunda número
     */
    inputTypes: [number, number];
    modal?: string;
    disabled?: boolean;
    required?: (string | null)[];
}

const FormDoubleInput: React.FC<IFormDoubleInputProps> = ({
    label,
    name,
    double = true,
    inputTypes,
    modal,
    disabled,
    required = []
}) => {
    const { register, formState: { errors } = {} } = useFormContext();
    const [show, setShow] = useState(false);
    const getInputId = (index: number) => `${name}-${index}`;
    const isDouble = double !== false;
    const errorLeft = get(errors, isDouble ? `${name}.0` : name);
    const errorRight = get(errors, `${name}.1`);
    const inputColClass = isDouble ? 'col-xl-4' : 'col-xl-8';
    const getFieldIndex = (inputName: string) => inputName.endsWith('.1') ? 1 : 0;
    const getRequiredRule = (index: number) => {
        const message = required?.[index];
        return message ? { value: true, message } : false;
    };

    const renderInputByType = (
        type: number,
        inputName: string,
        inputId: string,
        fieldError: any
    ) => {
        switch (type) {

            case 5: // YES / NO
                return (
                    <div className={`input-block input-double mb-1 mt-1 ${fieldError ? 'error' : ''}`}>
                        <select
                            id={inputId}
                            {...register(inputName, {
                                setValueAs: (v) => {
                                    if (v === '') return null;
                                    return v === 'true';
                                },
                                required: getRequiredRule(getFieldIndex(inputName))
                            })}
                            disabled={disabled}
                            defaultValue=""
                        >
                            <option value="">Selecciona una opción...</option>
                            <option value="true">Sí</option>
                            <option value="false">No</option>
                        </select>

                        {fieldError?.message && (
                            <p className="  ">{fieldError.message}</p>
                        )}
                    </div>
                );

            case 4: // FECHA
                return (
                    <div className={`input-block input-double mb-1 mt-1 ${fieldError ? 'error' : ''}`}>
                        <input
                            type="date"
                            id={inputId}
                            autoComplete='off'
                            spellCheck={false}
                            autoCapitalize='off'
                            autoCorrect='off'

                            {...register(inputName, {
                                required: getRequiredRule(getFieldIndex(inputName))
                            })}
                            disabled={disabled}
                        />

                        {fieldError?.message && (
                            <p className="">{fieldError.message}</p>
                        )}
                    </div>
                );

            case 3: // DECIMALES
                return (
                    <div className={`input-block input-double mb-1 mt-1 ${fieldError ? 'error' : ''}`}>
                        <input
                            type="text"
                            autoComplete='off'
                            spellCheck={false}
                            autoCapitalize='off'
                            autoCorrect='off'
                            id={inputId}
                            {...register(inputName, {
                                required: getRequiredRule(getFieldIndex(inputName)),
                                setValueAs: (v) => {
                                    const str = typeof v === 'string' ? v : String(v ?? '');
                                    return /^-?[0-9]+([.,][0-9]+)?$/.test(str)
                                        ? parseFloat(str.replace(',', '.'))
                                        : v;
                                },
                                pattern: {
                                    value: /^-?[0-9]+([.,][0-9]+)?$/,
                                    message: 'Introduce un número válido (entero o decimal)'
                                }
                            })}
                            disabled={disabled}
                        />

                        {fieldError?.message && (
                            <p className="">{fieldError.message}</p>
                        )}
                    </div>
                );

            case 2: // ENTEROS
                return (
                    <div className={`input-block input-double mb-1 mt-1 ${fieldError ? 'error' : ''}`}>
                        <input
                            type="text"
                            autoComplete='off'
                            spellCheck={false}
                            autoCapitalize='off'
                            autoCorrect='off'
                            id={inputId}
                            {...register(inputName, {
                                required: getRequiredRule(getFieldIndex(inputName)),
                                setValueAs: (v) => {
                                    if (v === '') return '';
                                    return /^[0-9]+$/.test(v) ? parseInt(v, 10) : v;
                                },
                                pattern: {
                                    value: /^[0-9]+$/,
                                    message: 'Solo se admiten números enteros'
                                }
                            })}
                            disabled={disabled}
                        />

                        {fieldError?.message && (
                            <p className="">{fieldError.message}</p>
                        )}
                    </div>
                );

            case 1: // TEXTO
            default:
                return (
                    <div className={`input-block input-double mb-1 mt-1 ${fieldError ? 'error' : ''}`}>
                        <input
                            type="text"
                            id={inputId}
                            autoComplete='off'
                            spellCheck={false}
                            autoCapitalize='off'
                            autoCorrect='off'
                            {...register(inputName, {
                                required: getRequiredRule(getFieldIndex(inputName))
                            })}
                            disabled={disabled}
                        />

                        {fieldError?.message && (
                            <p className="">{fieldError.message}</p>
                        )}
                    </div>
                );
        }
    };

    return (
        <div className="col-12 my-1">
            <div className="border rounded px-3 py-1" style={{ borderColor: '#ccc' }}>
                <div className="row align-items-center">

                    {/* LABEL */}
                    <div className="col-xl-4 mx d-flex align-items-center text-center justify-content-xl-start justify-content-center">
                        {modal && (
                            <div
                                className="info-icon-forms mx-1"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShow(true);
                                }}
                            >
                                <div className="information-icon-orange" />
                            </div>
                        )}
                        <label className="mb-0 fw-medium">{label}</label>

                    </div>

                    {/* INPUT 0 */}
                    <div className={inputColClass}>
                        {renderInputByType(
                            inputTypes[0],
                            isDouble ? `${name}.0` : name,
                            isDouble ? getInputId(0) : name,
                            errorLeft
                        )}
                    </div>

                    {isDouble && (
                        <div className="col-xl-4">
                            {renderInputByType(
                                inputTypes[1],
                                `${name}.1`,
                                getInputId(1),
                                errorRight
                            )}
                        </div>
                    )}

                </div>
            </div>

            <Modal show={show} onHide={() => setShow(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>{label}</Modal.Title>
                </Modal.Header>
                <Modal.Body>{modal}</Modal.Body>
            </Modal>
        </div>
    );
};

export default FormDoubleInput;
